import { isPreOrderActive } from '@cart-services/utils/findPreOrderProduct'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useRef, useState } from 'react'
import { Amount } from '../Amount/Amount'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const blinkKeyframes = stylex.keyframes({
  '0%': { opacity: 1 },
  '12.5%': { opacity: 0 },
  '25%': { opacity: 1 },
  '37.5%': { opacity: 0 },
  '50%': { opacity: 1 },
  '62.5%': { opacity: 0 },
  '75%': { opacity: 1 },
  '87.5%': { opacity: 0 },
  '100%': { opacity: 1 },
})

const styles = stylex.create({
  wrapper: {
    padding: '0.5rem',
    alignItems: 'center',
    columnGap: '0.5rem',
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: '3.25rem',
  },
  title: {
    color: colors.grey800,
    fontSize: '0.875rem',
    fontWeight: 600,
    maxWidth: {
      [MOBILE]: '11.25rem',
      default: '100%',
    },
  },
  prices: {
    flex: '1',
    columnGap: '0.25rem',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  remaining: {
    color: colors.green400,
    fontSize: '0.625rem',
    width: '100%',
  },
  outStock: {
    color: colors.red400,
    display: 'inline-block',
    fontSize: '0.875rem',
    textAlign: 'center',
    width: '6rem',
  },
  blink: {
    animationDuration: '1s',
    animationIterationCount: 1,
    animationName: blinkKeyframes,
  },
})

export interface CollapseProductItemProps {
  id: string
  variation: Variation
  currentQty: number
  onQuantityChange?: (quantity?: number) => void
  /** 是否顯示預購標籤 */
  showPreOrderChip?: boolean
}

export const CollapseProductItem = (props: CollapseProductItemProps) => {
  const { id, variation, currentQty, onQuantityChange, showPreOrderChip = false } = props
  const [hasClassName, setHasClassName] = useState(false)
  const blinkRef = useRef<HTMLDivElement>(null)

  const isShowOriginalPrice
    = variation.original_price !== 0 && variation.original_price > variation.sale_price

  const remainingQty = variation.stock - currentQty

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (blinkRef.current?.classList.contains('search-product-selected')) {
        setHasClassName(true)
      }
    })

    if (blinkRef.current) {
      observer.observe(blinkRef.current, {
        attributes: true,
        attributeFilter: ['class'],
      })
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={blinkRef}
      {...stylex.props(
        styles.wrapper,
        hasClassName ? styles.blink : {},
      )}
      id={id}
    >
      <HStack alignItems="center" gap={1}>
        <h5 {...stylex.props(styles.title)}>{variation?.name}</h5>
        {showPreOrderChip && isPreOrderActive(variation.pre_order_info) && (
          <Chip variant="square" size="small" color="red" isOutlined isSoft>
            預購
          </Chip>
        )}
      </HStack>
      <div {...stylex.props(styles.prices)}>
        {isShowOriginalPrice && (
          <Amount
            amount={variation.original_price}
            isLineThrough={true}
            color={colors.grey500}
          />
        )}
        <Amount
          amount={variation.sale_price}
          color={colors.primary600}
          isBold={true}
        />
        {remainingQty <= 25 && !variation.is_out_of_stock && (
          <span {...stylex.props(styles.remaining)}>
            剩餘數量
            {remainingQty}
          </span>
        )}
      </div>
      {
        variation.is_out_of_stock
          ? <span {...stylex.props(styles.outStock)}>已售完</span>
          : (
            <NumberInput
              max={variation.stock}
              value={currentQty}
              onChange={onQuantityChange}
            />
          )
      }
    </div>
  )
}
